import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AdminSubscriptionList, OfferList, PromotionList } from '@/api/generated';
import { ApiError } from '@/api/errors';
import { enumParam, stringParam, useUrlState } from '@/components/useUrlState';
import { useTenantApi } from '@/tenant/useTenant';
import { STATUS_FILTERS, filterToQuery, type StatusFilter } from './subscriberStatus';

/** The list's state in the URL (TMM-B6): `?q=` and `?status=`, defaults omitted. */
const SCHEMA = {
  q: stringParam(),
  status: enumParam<StatusFilter>(STATUS_FILTERS, 'all'),
};

export interface SubscribersData {
  list: AdminSubscriptionList;
  /** `null` while offers are loading, or when that request failed. */
  offers: OfferList | null;
  /** `null` while promotions are loading, or when that request failed. */
  promotions: PromotionList | null;
}

/** A side load. A failure leaves the subscriber list up. */
export interface SidePart {
  status: 'loading' | 'ready' | 'error';
  error: unknown;
  reload: () => void;
}

export interface SubscribersState {
  /** The list. Offers and promotions have their own status. */
  status: 'loading' | 'ready' | 'error';
  data: SubscribersData | null;
  error: ApiError | null;
  /** The status filter, in the URL (`?status=`). */
  filter: StatusFilter;
  /** The search box's text, as typed. The URL's `?q=` (and the load) follow it, debounced. */
  query: string;
  setFilter: (filter: StatusFilter) => void;
  setQuery: (query: string) => void;
  /** A search or a status filter is on: an empty list then says nothing matches (FEED-002). */
  filtered: boolean;
  /** Clears the search and the status filter (the empty list's Clear filters). */
  clearFilters: () => void;
  /** Refetches the list. */
  reload: () => void;
  /** Refetches the list without the loading state (TMMRSL-0056). */
  refresh: () => Promise<void>;
  offers: SidePart;
  promotions: SidePart;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

function looksLikeEmail(value: string): boolean {
  return value.includes('@');
}

function listParams(filter: StatusFilter, query: string) {
  const trimmed = query.trim();
  const params = {
    ...filterToQuery(filter),
    ...(looksLikeEmail(trimmed) ? { email: trimmed } : {}),
  };
  return Object.keys(params).length > 0 ? params : undefined;
}

/**
 * Loads the subscriber list and, independently, offers and promotions. A side-load failure
 * leaves the list up (TMMRSL-0055). The list refetches when the server-side query changes.
 */
export function useSubscribers(): SubscribersState {
  const [status, setStatus] = useState<SubscribersState['status']>('loading');
  const [list, setList] = useState<AdminSubscriptionList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [offers, setOffers] = useState<OfferList | null>(null);
  const [offersStatus, setOffersStatus] = useState<SidePart['status']>('loading');
  const [offersError, setOffersError] = useState<unknown>(null);
  const [promotions, setPromotions] = useState<PromotionList | null>(null);
  const [promotionsStatus, setPromotionsStatus] = useState<SidePart['status']>('loading');
  const [promotionsError, setPromotionsError] = useState<unknown>(null);
  const url = useUrlState(SCHEMA, { search: 'q' });
  const filter = url.values.status;
  // The server reads the debounced query; the list narrows by the typed text at once.
  const query = url.values.q;
  const api = useTenantApi();
  const listSeq = useRef(0);

  const serverKey = useMemo(() => {
    const trimmed = query.trim();
    const email = looksLikeEmail(trimmed) ? trimmed : '';
    return `${filter}|${email}`;
  }, [filter, query]);

  const reload = useCallback(() => {
    const token = ++listSeq.current;
    setStatus('loading');
    setError(null);
    const params = listParams(filter, query);
    api.listSubscriptions(params).then(
      (nextList) => {
        if (listSeq.current !== token) return;
        setList(nextList);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        if (listSeq.current !== token) return;
        setList(null);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api, filter, query]);

  const refresh = useCallback(async () => {
    const token = ++listSeq.current;
    const params = listParams(filter, query);
    try {
      const nextList = await api.listSubscriptions(params);
      if (listSeq.current !== token) return;
      setList(nextList);
      setError(null);
      setStatus('ready');
    } catch (err) {
      if (listSeq.current !== token) return;
      throw err;
    }
  }, [api, filter, query]);

  const reloadOffers = useCallback(() => {
    setOffersStatus('loading');
    setOffersError(null);
    api.getOffers().then(
      (next) => {
        setOffers(next);
        setOffersError(null);
        setOffersStatus('ready');
      },
      (err: unknown) => {
        setOffers(null);
        setOffersError(err);
        setOffersStatus('error');
      },
    );
  }, [api]);

  const reloadPromotions = useCallback(() => {
    setPromotionsStatus('loading');
    setPromotionsError(null);
    api.listPromotions().then(
      (next) => {
        setPromotions(next);
        setPromotionsError(null);
        setPromotionsStatus('ready');
      },
      (err: unknown) => {
        setPromotions(null);
        setPromotionsError(err);
        setPromotionsStatus('error');
      },
    );
  }, [api]);

  useEffect(() => {
    reload();
  }, [reload, serverKey]);

  useEffect(() => {
    reloadOffers();
  }, [reloadOffers]);

  useEffect(() => {
    reloadPromotions();
  }, [reloadPromotions]);

  const data: SubscribersData | null = list ? { list, offers, promotions } : null;

  return {
    status,
    data,
    error,
    filter,
    query: url.searchText,
    setFilter: (next) => url.set({ status: next }),
    setQuery: url.setSearchText,
    filtered: filter !== 'all' || url.searchText.trim() !== '',
    clearFilters: () => {
      url.setSearchText('');
      url.set({ q: '', status: 'all' });
    },
    reload,
    refresh,
    offers: { status: offersStatus, error: offersError, reload: reloadOffers },
    promotions: { status: promotionsStatus, error: promotionsError, reload: reloadPromotions },
  };
}
